import { useCallback, useEffect, useState } from 'react';
import { SideNav } from './components/SideNav.jsx';
import ProgramsPage from './pages/ProgramsPage.jsx';
import UniversitiesPage from './pages/UniversitiesPage.jsx';

/**
 * Two search pages, one shell.
 *
 * Routing is a three-line pushState switch rather than a router dependency:
 * there are exactly two routes, both are top-level, and neither has params.
 * `/search-program` is Search Programs; `/universities` is the university view.
 * `/` (or any unknown path) redirects to `/search-program` on load so the two
 * pages have matching explicit URLs. The tab bar and the sidebar both drive it,
 * and the browser back button works because every navigation goes through
 * history.
 */
const DEFAULT_ROUTE = '/search-program';

const ROUTES = {
  '/search-program': { key: 'programs', label: 'Search Programs' },
  '/universities': { key: 'universities', label: 'Universities' },
};

function routeFor(pathname) {
  return ROUTES[pathname] ? pathname : DEFAULT_ROUTE;
}

// Canonicalise any unknown landing URL before React's first render.
// Keep `/` intact for browser back-button history parity with root navigation.
// `/chat` is main.jsx's page, not this shell's: main.jsx imports this module
// before it reads the path, so rewriting it here sent /chat to /search-program.
const CHAT_PATH = /^\/chat\/?$/;
if (window.location.pathname !== '/' && !ROUTES[window.location.pathname] && !CHAT_PATH.test(window.location.pathname)) {
  window.history.replaceState(null, '', DEFAULT_ROUTE);
}

export default function App() {
  const [path, setPath] = useState(() => routeFor(window.location.pathname));

  useEffect(() => {
    const onPop = () => setPath(routeFor(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to) => {
    if (routeFor(to) === path) return;
    window.history.pushState(null, '', to);
    setPath(routeFor(to));
  }, [path]);

  const current = ROUTES[path];

  return (
    <>
      <header className="app-header">
        <div className="header-left">
          <div className="logo" onClick={() => navigate(DEFAULT_ROUTE)} role="banner">
            <span className="crizac-brand-logo">Crizac</span>
          </div>

          <nav className="tabs" aria-label="Primary">
            {Object.entries(ROUTES).map(([to, r]) => (
              <a
                key={r.key}
                href={to}
                className={r.key === current.key ? 'on' : undefined}
                aria-current={r.key === current.key ? 'page' : undefined}
                onClick={(e) => { e.preventDefault(); navigate(to); }}
              >
                {r.label}
              </a>
            ))}
          </nav>
        </div>

        <div className="header-right">
          <div className="portal-badge-wrap">
            <span className="portal-badge-text">Crizac Portal</span>
          </div>
        </div>
      </header>

      <div className="shell">
        <SideNav current={current.key} onNavigate={navigate} />

        <main id="mainContent">
          {current.key === 'universities' ? <UniversitiesPage /> : <ProgramsPage />}
        </main>
      </div>
    </>
  );
}
